import { useMemo } from 'react';
import { DropdownProps, MenuProps } from 'antd';
import { ToolbarButtonProps } from './ToolbarButton';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  TextNode,
} from 'lexical';
import { $patchStyleText } from '@lexical/selection';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BgColorsOutlined,
  ClearOutlined,
  LineHeightOutlined,
  StrikethroughOutlined,
} from '@ant-design/icons';
import { ToolbarDropdownButton, renderMenuItem } from './ToolbarDropdownButton';
import styles from '../RichTextEditor.module.scss';
import { Icon } from '@/components/Icon';
import { useTranslation } from 'react-i18next';

export type TextTransformType =
  | 'lowercase'
  | 'uppercase'
  | 'capitalize'
  | 'strikethrough'
  | 'subscript'
  | 'superscript'
  | 'highlight'
  | 'clear';

export interface ToolbarTextTransformDropdownButtonProps
  extends ToolbarButtonProps,
    Pick<DropdownProps, 'trigger' | 'overlay'> {
  selected?: TextTransformType;
}

export const TEXT_TRANSFORM_META_BASE: Record<
  TextTransformType,
  { icon: React.ReactNode; shortcut?: string }
> = {
  lowercase: { icon: <ArrowDownOutlined /> },
  uppercase: { icon: <ArrowUpOutlined /> },
  capitalize: { icon: <LineHeightOutlined /> },
  strikethrough: { icon: <StrikethroughOutlined /> },
  subscript: { icon: <Icon type="subscript" /> },
  superscript: { icon: <Icon type="superscript" /> },
  highlight: { icon: <BgColorsOutlined /> },
  clear: { icon: <ClearOutlined /> },
};

export function useLocalizedTextTransformMeta() {
  const { t } = useTranslation();

  return useMemo(() => {
    const entries = Object.entries(TEXT_TRANSFORM_META_BASE);

    return Object.fromEntries(
      entries.map(([key, val]) => [
        key,
        {
          icon: val.icon,
          shortcut: val.shortcut,
          label: t(`editor.textTransform.${key}`),
        },
      ]),
    ) as Record<
      TextTransformType,
      { label: string; icon: React.ReactNode; shortcut?: string }
    >;
  }, [t]);
}

function applyCaseTransform(type: TextTransformType) {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return;

  const nodes = selection.getNodes();
  nodes.forEach((node) => {
    if (node instanceof TextNode) {
      const text = node.getTextContent();
      let updated = text;

      if (type === 'lowercase') updated = text.toLowerCase();
      if (type === 'uppercase') updated = text.toUpperCase();
      if (type === 'capitalize')
        updated = text.replace(/\b\w/g, (c) => c.toUpperCase());

      node.setTextContent(updated);
    }
  });
}

function applyHighlight(
  color: string = '#fff8b3',
  borderColor: string = '#f1c40f',
) {
  const selection = $getSelection();
  if ($isRangeSelection(selection)) {
    $patchStyleText(selection, {
      'background-color': color,
      'border-bottom': `2px solid ${borderColor}`,
    });
  }
}

function clearFormatting() {
  const selection = $getSelection();
  if ($isRangeSelection(selection)) {
    const nodes = selection.getNodes();
    nodes.forEach((node) => {
      if (node instanceof TextNode) {
        node.setFormat(0);
        node.setStyle('');
      }
    });
  }
}

export function runTextTransform(
  editor: LexicalEditor,
  type: TextTransformType,
) {
  editor.update(() => {
    switch (type) {
      case 'lowercase':
      case 'uppercase':
      case 'capitalize':
        return applyCaseTransform(type);

      case 'strikethrough':
        return editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');

      case 'subscript':
        return editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');

      case 'superscript':
        return editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');

      case 'highlight':
        return applyHighlight();

      case 'clear':
        return clearFormatting();
    }
  });
}

export function useTextTransformMenu(editor: LexicalEditor) {
  const meta = useLocalizedTextTransformMeta();

  const items = useMemo<MenuProps['items']>(() => {
    return (Object.keys(meta) as TextTransformType[]).map((type) => ({
      key: type,
      label: renderMenuItem(
        meta[type].icon,
        meta[type].label,
        meta[type].shortcut,
      ),
      onClick: () => runTextTransform(editor, type),
    }));
  }, [editor, meta]);

  return { textTransformMenu: items, meta };
}

export function ToolbarTextTransformDropdownButton(
  props: ToolbarTextTransformDropdownButtonProps,
) {
  const { selected, ...dropDownProps } = props;
  const meta = useLocalizedTextTransformMeta();
  const selectedMeta = selected ? meta[selected] : undefined;

  return (
    <ToolbarDropdownButton
      {...dropDownProps}
      selectedItem={selectedMeta}
      className={styles.textTransformButton}
    />
  );
}
